import type { Dictionary } from "./dictionary.ts";

export const de = {
	app: {
		name: "CMS-Analyse",
		language: "Sprache",
		localeName: { de: "Deutsch", en: "Englisch" },
	},
	nav: {
		dashboard: "Übersicht",
		catalog: "Katalog",
		products: "Produkte",
		personas: "Personas",
		questionnaires: "Fragebögen",
		authors: "Ansprechpartner",
		views: "Ansichten",
		overview: "Antwortmatrix",
		compare: "Vergleich",
		reports: "Berichte",
		customers: "Kunden",
		start: "Start",
		persona: "Persona",
		logout: "Abmelden",
		collapse: "Navigation ausblenden",
		expand: "Navigation einblenden",
	},
	auth: {
		title: "Anmeldung",
		token: "Zugangstoken",
		signIn: "Anmelden",
		signingIn: "Wird angemeldet",
		refused: "Das Token wurde abgelehnt.",
	},
	preview: {
		viewing: "Sie sehen die Ansicht von",
		exit: "Vorschau beenden",
		readOnly: "In der Vorschau ist nichts veränderbar.",
		enter: "Als Kunde ansehen",
	},
	customers: {
		title: "Kunden",
		newCustomer: "Neuer Kunde",
		users: "Nutzer",
		access: "Zugriff",
		history: "Verlauf",
		team: "Team",
		teamIntro:
			"Berater ohne Kunde. Jeder erhält ein eigenes Token, damit der Verlauf Personen nennt statt eines gemeinsamen Schlüssels.",
		products: "Produkte im Zugriff",
		productsHint:
			"Leer heißt keine. Produkte ohne Antworten bleiben für den Kunden unsichtbar, damit niemand leere Kästchen sieht.",
		withoutData: "ohne Daten",
		assign: "Zuordnen",
		unassign: "Zuordnung lösen",
		nothingAssigned: "Noch nichts zugeordnet.",
		noneFree: "Es gibt nichts Freies mehr, das sich zuordnen ließe.",
		openQuestionnaires: "Offene Fragebögen",
		newestReport: "Neuester Bericht",
		userCount: "Nutzer",
		inactive: "Inaktiv",
		relevancyQuestionnaires: "Relevanzfragebögen",
		notFound: "Diesen Kunden gibt es nicht.",
	},
	users: {
		newUser: "Neuer Nutzer",
		role: "Rolle",
		roleAdmin: "Berater (Admin)",
		roleCustomer: "Kunde",
		author: "Ansprechpartner",
		authorHint:
			"Optional. Verknüpft den Nutzer mit dem Ansprechpartner, an den Fragebögen adressiert sind.",
		tokenPrefix: "Token beginnt mit",
		lastSeen: "Zuletzt gesehen",
		never: "Noch nie",
		issuedTitle: "Zugangstoken",
		issuedIntro:
			"Geben Sie dieses Token sicher weiter. Es wird nicht erneut angezeigt.",
		understood: "Ich habe das Token gesichert",
		rotate: "Token erneuern",
		rotateHint: "Das bisherige Token und alle Sitzungen werden ungültig.",
		revoke: "Token entziehen",
		revoked: "Entzogen",
		none: "Noch keine Nutzer.",
	},
	portal: {
		startTitle: "Start",
		open: "Offene Fragebögen",
		noOpen: "Nichts offen. Alle Fragebögen sind eingereicht.",
		fill: "Ausfüllen",
		reports: "Berichte",
		noReports: "Noch keine Berichte.",
		compare: "Zum Vergleich",
		compareHint: "Ihre Personas sind bereits vorausgewählt.",
		empty: "Ihr Berater hat noch keine Daten freigegeben.",
		draft: "Entwurf",
		lockedOn: "Festgeschrieben am",
		submittedOn: "Eingereicht am",
		personaTitle: "Persona",
		ratings: "Gewichtung",
		noRatings: "Noch keine Gewichtung festgelegt.",
		ratingsOpen:
			"Der Relevanzfragebogen ist noch offen. Die Gewichtung erscheint hier, sobald er eingereicht und übernommen ist.",
		requirements: "Muss-Kriterien",
		noRequirements: "Keine Muss-Kriterien festgelegt.",
		fillRelevancy: "Relevanzfragebogen ausfüllen",
		compareFor: "Vergleich für diese Persona",
		noPersona: "Ihnen ist noch keine Persona zugeordnet.",
		noWeighting:
			"Ohne Persona zeigt der Vergleich die ungewichteten Bewertungen.",
		noProducts: "Es sind noch keine Produkte für Sie freigegeben.",
	},
	common: {
		of: "von",
		optional: "optional",
		retry: "Erneut versuchen",
		loading: "Wird geladen",
		saving: "Wird gespeichert",
		saved: "Gespeichert",
		saveFailed: "Speichern fehlgeschlagen",
		notAssessed: "N/A",
		coverage: "Abdeckung",
		score: "Bewertung",
		band: "Stufe",
		answer: "Antwort",
		yes: "Ja",
		no: "Nein",
		dontKnow: "Weiß ich nicht",
		none: "Keine",
		all: "Alle",
		create: "Anlegen",
		save: "Speichern",
		cancel: "Abbrechen",
		copy: "Kopieren",
		copied: "Kopiert",
		edit: "Bearbeiten",
		add: "Hinzufügen",
		new: "Neu",
		empty: "Nichts vorhanden",
		identity: "Kennung",
		name: "Name",
		title: "Titel",
		label: "Bezeichnung",
		description: "Beschreibung",
		active: "Aktiv",
		status: "Status",
		product: "Produkt",
		topic: "Thema",
		capability: "Eigenschaft",
		question: "Frage",
		persona: "Persona",
		author: "Autor",
		weight: "Gewicht",
		actions: "Aktionen",
		resizeColumn: "Spaltenbreite",
		resizeColumnHint:
			"Ziehen oder mit den Pfeiltasten ändern. Doppelklick stellt die automatischen Breiten wieder her.",
		notAvailable: "Nicht verfügbar",
		customer: "Kunde",
		customerHint:
			"Ohne Kunde bleibt der Eintrag intern und ist nur für Berater sichtbar.",
		internal: "Intern",
		noCustomer: "Kein Kunde (intern)",
	},
	band: {
		missing: "Fehlt oder schwach",
		partial: "Teilweise",
		solid: "Solide",
		excellent: "Ausgezeichnet",
	},
	error: {
		network: "Die Verbindung ist fehlgeschlagen",
		decode: "Die Antwort konnte nicht eingelesen werden.",
		notFound: "Nicht gefunden",
		validation: "Ungültige Eingabe.",
		conflict: "Der aktuelle Zustand lässt die Eingabe nicht zu.",
		inUse: "Wird noch verwendet",
		unauthorized: "Die Anmeldung fehlt oder ist abgelaufen.",
		forbidden: "Dafür fehlt die Berechtigung.",
		internal: "Der Server hat die Anfrage nicht beantwortet.",
	},
	remove: {
		action: "Löschen",
		title: "Wirklich löschen?",
		confirm: "Endgültig löschen",
		deleting: "Wird gelöscht …",
		blockedTitle: "Wird noch verwendet",
		blockedIntro: "Diese Einträge verweisen noch darauf:",
		cascadeWarning: "wird mitgelöscht",
		detachNote: "Verweis wird entfernt",
		forceLabel: "Trotzdem löschen",
		forceHint:
			"Einträge, die ohne diesen Eintrag keinen Sinn ergeben, werden mitgelöscht. Bei allen anderen wird nur der Verweis entfernt.",
		forceConfirm: "Mit allen Abhängigkeiten löschen",
		done: "Gelöscht",
	},
	answer: {
		sourceHuman: "Manuell",
		sourceAi: "KI",
		confidenceHigh: "Sicher",
		confidenceLow: "Unsicher",
		history: "Antwortverlauf",
		validFrom: "Gültig ab",
		validTo: "Gültig bis",
		value: "Wert",
		noAnswers: "Für dieses Produkt liegen noch keine Antworten vor.",
	},
	dashboard: {
		title: "Übersicht",
		questionnaires: "Fragebögen",
		changes: "Letzte Änderungen",
		entity: "Objekt",
		actor: "Ausgelöst von",
		at: "Zeitpunkt",
		scores: "Produkte mit Bewertung",
		scoresFromLocked: "Aus dem letzten festgeschriebenen Bericht.",
		scoresFromLive: "Live berechnet, noch nicht festgeschrieben.",
		noEvaluation: "Noch keine Bewertung vorhanden. Erstelle einen Bericht.",
	},
	compare: {
		title: "Produkte vergleichen",
		productA: "Produkt A",
		productB: "Produkt B",
		swap: "Tauschen",
		level: "Ebene",
		levelTopic: "Themen",
		levelCapability: "Eigenschaften",
		axes: "Achsen",
		allAxes: "Alle",
		noAxes: "Keine",
		noAxesAvailable: "Für dieses Paar ist nichts bewertet.",
		noAxesPicked: "Wähle mindestens eine Achse aus.",
		reset: "Größte Unterschiede",
		samePair: "Wähle zwei verschiedene Produkte.",
		tableCaption: "Bewertungen der beiden Produkte je Achse.",
		view: "Ansicht",
		viewHint:
			"Eine Ansicht grenzt Produkte und Themen ein und wird live ausgewertet.",
		newestReport: "Neuester Bericht",
		sourceView: "Bewertungen aus einer Live-Auswertung der gewählten Ansicht.",
		tooFew:
			"Hier sind weniger als zwei Produkte bewertet, es gibt nichts zu vergleichen.",
		differences: "Wo sie sich unterscheiden",
		differencesIntro:
			"Die gewählten Achsen nach Abstand sortiert, der größte zuerst. Der Balken zeigt, wie weit die beiden auseinanderliegen, und in welche Richtung.",
		gap: "Abstand",
		leads: "Führt",
		noDifferences: "Auf den gewählten Achsen liegen die beiden gleichauf.",
		tiedCount: "Gleichauf",
		oneSidedCount: "Nur für ein Produkt bewertet",
		partsCapabilities: "Eigenschaften in diesem Thema",
		partsQuestions: "Fragen hinter dieser Eigenschaft",
		noParts: "Zu dieser Achse ist keine Frage hinterlegt.",
		partTied: "Gleichauf",
		partOneSided: "Nur eine Seite bewertet",
		persona: "Gewichtung",
		personaNone: "Ohne Persona-Gewichtung",
		personaHint: "Zeigt, was die Gewichtung dieser Persona aus dem Vergleich macht.",
		weightedFor: "gewichtet für",
		weightedUp: "steigt durch die Gewichtung",
		weightedDown: "sinkt durch die Gewichtung",
		weightedLevel: "unverändert durch die Gewichtung",
		weightHeader: "Bedeutung",
		weightOf: "Bedeutung für",
		weightedGap: "Abstand nach Gewichtung",
		weightedGapKey:
			"Die dünne Linie darunter ist derselbe Abstand nach der Gewichtung: Eine Achse mit ++ zählt doppelt, eine mit -- gar nicht.",
		notRated: "hat noch keine Gewichtung abgegeben. Bis dahin zählt jede Achse gleich.",
		explanation: "Begründung",
		showExplanation: "Begründung lesen",
		noExplanation: "Zu dieser Antwort wurde keine Begründung hinterlegt.",
		noAnswer: "Für dieses Produkt liegt hier keine Antwort vor.",
		comment: "Anmerkung",
		answeredAs: "Angegeben als",
		inCapability: "Frage zur Eigenschaft",
	},
	history: {
		title: "Änderungsprotokoll",
		link: "Verlauf",
		at: "Zeitpunkt",
		actor: "Ausgelöst von",
		change: "Änderung",
		asOfJump: "Bericht zu diesem Stand",
		noEvents: "Für dieses Objekt ist noch keine Änderung protokolliert.",
		created: "Angelegt",
		updated: "Geändert",
		deleted: "Gelöscht",
		translation: "Übersetzung",
		draftAnswer: "Antwortentwurf",
		unknown: "Unbekannte Änderung",
	},
	tree: {
		selectAll: "Ganzer Katalog",
		selected: "ausgewählt",
		expand: "Aufklappen",
		collapse: "Zuklappen",
		expandAll: "Alles aufklappen",
		collapseAll: "Alles zuklappen",
		clear: "Nichts auswählen",
		unassigned: "Keinem Thema zugeordnet",
		empty: "Der Katalog enthält noch keine Fragen.",
	},
	coverage: {
		label: {
			counted: "Gewertet",
			outside: "Nicht in Ansicht",
			unanswered: "Unbeantwortet",
		},
		hint: {
			counted:
				"Mindestens eine Antwort liegt in dieser Ansicht und erreicht damit den Bericht.",
			outside:
				"Beantwortet, aber diese Ansicht erreicht keine dieser Antworten. Meist leitet keine Eigenschaft im Umfang diese Frage ab.",
			unanswered:
				"Für diese Frage wurde noch nie eine Antwort erfasst, für kein Produkt.",
		},
		view: "Gemessen an Ansicht",
		viewHint:
			"Die Abdeckung wird an dieser Ansicht gemessen. Ohne Auswahl an einer Ansicht, die nichts einschränkt.",
		noView: "Keine Ansicht",
	},
	views: {
		title: "Ansichten",
		newView: "Neue Ansicht",
		topics: "Themen",
		capabilities: "Eigenschaften",
		personas: "Personas",
		products: "Produkte",
		questionnaires: "Frageb\u00f6gen",
		allHint:
			"Ausschalten, um die Ansicht auf einzelne Eintr\u00e4ge zu begrenzen.",
		hollow:
			"Es ist nichts ausgew\u00e4hlt, das w\u00e4re dasselbe wie \u201aalle\u2018. Bitte mindestens einen Eintrag w\u00e4hlen.",
		narrows: "Begrenzt auf",
		everything: "Alles",
		none: "Es gibt noch keine Ansicht, ein Bericht braucht aber eine.",
	},
	overview: {
		title: "Antwortmatrix",
		caveat:
			"Diese Mittelwerte sind keine Berichtswerte. Der Mittelwert eines Themas ist das einfache Mittel der Fragen, die ein Produkt beantwortet hat — ohne Gewichtung der Eigenschaften und ohne Relevanz.",
		topicAverage: "Mittelwert des Themas",
		average: "Mittelwert",
		answeredOf: "beantwortet von",
		origin: "Herkunft",
		noAnswerYet: "Keine Antwort",
		unassigned: "Fragen ohne Thema",
		unassignedHint:
			"Kein Thema führt diese Fragen, also erscheinen sie in keinem Baum und in keinem Bericht. Gezeigt wird nur die Anzahl der vorliegenden Antworten: ohne Thema wurde keine Vorrangregel angewendet, keine davon lässt sich also als die geltende Antwort bezeichnen. Weise der Frage ein Thema zu, um das zu beheben.",
		recorded: "vorliegend",
		onlyAnswered: "Nur Fragen mit Antworten",
		onlyAnsweredHint:
			"Blendet alles aus, was in keinem Produkt beantwortet ist.",
		noProducts: "Es gibt noch kein Produkt, also nichts zu vergleichen.",
		empty: "Noch trägt keine Frage eine Antwort.",
	},
	catalog: {
		title: "Katalog",
		relevancy: "Relevanz",
		capabilities: "Eigenschaften",
		questions: "Fragen",
		questionsLinked: "verknüpfte Fragen",
		informational: "Nur Kontext",
		newTopic: "Thema anlegen",
		newCapability: "Eigenschaft anlegen",
		newQuestion: "Frage anlegen",
		answerType: "Antworttyp",
		linkedQuestions: "Verknüpfte Fragen",
		linkedQuestionsHint: "Fragen mehreren Eigenschaften zugeteilt sein.",
		topicQuestions: "Fragen in diesem Thema",
		topicQuestionsHint:
			"Die Fragen ankreuzen, die dieses Thema f\u00fchrt. Eine Frage erscheint im Baum nur \u00fcber ein Thema, das sie f\u00fchrt.",
		capabilityDescriptionHint:
			"Wird Empf\u00e4ngern im Relevanz-Fragebogen angezeigt. Beschreiben, was die Eigenschaft umfasst, damit eine Bewertung nicht nur am Label h\u00e4ngt.",
		owningTopicHint: "Das Thema, unter dem diese Eigenschaft bewertet wird.",
		questionTopicHint:
			"Das Thema, das diese Frage f\u00fchrt. Ein anderes Thema verschiebt sie.",
		questionTopicRequired:
			"Ohne Thema w\u00fcrde die Frage im Baum gar nicht erscheinen.",
		unassignedCapabilities: "Eigenschaften ohne Thema",
		unassignedCapabilitiesHint:
			"Eine Eigenschaft erscheint im Baum nur über das Thema, das sie nennt. Diese nennen keines oder eines, das nicht mehr existiert; sie liegen damit außerhalb des Katalogs und kein Bericht erreicht die Fragen, aus denen sie abgeleitet sind. Weise jeder ein Thema zu.",
		danglingTopic: "Nennt Thema",
		capabilityTopicRequired:
			"Ohne Thema erscheint die Eigenschaft nirgends im Katalog, und kein Bericht erreicht die Fragen, aus denen sie abgeleitet ist.",
		compareAnswers: "Antworten vergleichen",
		compareAnswersHint:
			"Alle Produkte zu dieser einen Frage, Zeile für Zeile: die Antwortmatrix auf eine Frage verengt.",
		search: "Suche",
		searchHint: "Durchsuche den Katalog nach Themen, Eigenschaften und Fragen.",
		searchPlaceholder: "Teil eines Titels eingeben",
		noMatches: "Nichts im Katalog kommt dem nahe.",
	},
	products: {
		title: "Produkte",
		newProduct: "Produkt anlegen",
		bandOverview: "Stufen pro Thema",
		chooseView: "Ansicht",
		viewHint: "Die Stufen stammen aus einer Auswertung dieser Ansicht.",
		references: "Referenzprojekte",
		overview: "Übersicht",
		answers: "Antworten",
		dossier: "Dossier",
		details: "Stammdaten",
		tags: "Tags",
		tagsHint:
			"Kurze Schlagworte zur Orientierung, etwa EU-Hosting, Deutschland oder Open Source. Eingabetaste oder Komma fügt eines hinzu.",
		tagsLabel: "Tags dieses Produkts",
		addTag: "Hinzufügen",
		removeTag: "Tag entfernen",
		noTags: "Noch keine Tags.",
		noReferences: "Noch keine Referenzprojekte.",
		noDescription: "Noch keine Beschreibung.",
		noDossier: "Zu diesem Produkt gibt es noch kein Dossier.",
		editDossier: "Dossier bearbeiten",
		dossierHint:
			"Markdown: Überschriften, Listen, Tabellen, fett, kursiv und Links. Ein Vorspann zwischen zwei Zeilen aus drei Bindestrichen ganz oben wird zum Steckbrief.",
		write: "Schreiben",
		preview: "Vorschau",
		unsaved: "Ungespeicherte Änderungen",
		editDetails: "Stammdaten bearbeiten",
	},
	personas: {
		title: "Personas und Anforderungen",
		newPersona: "Persona anlegen",
		requirements: "Anforderungen",
		newRequirement: "Anforderung anlegen",
		minimumKind: "Schwelle",
		minimumScore: "Mindestbewertung",
		minimumBand: "Mindeststufe",
		rationale: "Begründung",
		rationaleHint:
			"Sie macht den Ausschluss verteidigbar und ist deshalb Pflicht.",
		rationaleRequired:
			"Ohne Begründung kann die Anforderung nicht angelegt werden.",
		view: "Ansicht",
	},
	authors: {
		title: "Ansprechpartner",
		newAuthor: "Ansprechpartner anlegen",
		email: "E-Mail",
		comment: "Anmerkung",
	},
	questionnaires: {
		title: "Fragebögen",
		workbook: "Arbeitsmappe",
		workbookHint:
			"Die Arbeitsmappe wird für ein Produkt geschrieben. Ausgefüllt lässt sie sich wieder einlesen.",
		workbookProduct: "Produkt",
		newQuestionnaire: "Fragebogen anlegen",
		productHint:
			"Das eine Produkt, das diese Antworten beschreiben. Die anderen sieht der Empfänger nie.",
		noProduct:
			"Dieser Fragebogen nennt kein Produkt und kann daher nicht beantwortet oder exportiert werden.",
		scope: "Umfang",
		scopeHint:
			"Ganzen Katalog, einen Teilbaum oder einzelne Fragen auswählen. Bei einem ausgewählten Thema gehören später ergänzte Fragen ebenfalls dazu.",
		noAuthors:
			"Es gibt noch keinen Ansprechpartner, ein Fragebogen braucht aber einen.",
		addAuthor: "Ansprechpartner anlegen",
		answers: "Diesem Fragebogen zugeordnete Antworten",
		answersHint:
			"Alle erfassten Antworten. Enthält Daten, die via MCP eingefügt wurden.",
		noAnswers: "Noch keine Antwort trägt diesen Fragebogen.",
		magicLink: "Magic Link",
		copyLink: "Link kopieren",
		review: "Antworten prüfen",
		accept: "Antworten übernehmen",
		accepted: "Übernommen",
		drafts: "Eingereichte Antworten",
		noDrafts: "Es wurde noch nichts eingetragen.",
		statusPending: "Offen",
		statusReceived: "Geöffnet",
		statusAnswered: "Eingereicht",
		statusEvaluated: "Übernommen",
		created: "Angelegt",
	},
	relevancy: {
		title: "Relevanz",
		ratings: "Bewertungen",
		noRatings: "Noch keine Bewertung.",
		rating: "Stufe",
		note: "Anmerkung",
		weight: "Gewicht",
		weighting: "Gewichtung",
		newQuestionnaire: "Relevanzbogen anlegen",
		scope: "Umfang",
		scopeHint:
			"Themen ankreuzen, um deren Eigenschaften zu fragen. Ohne Auswahl gilt die Ansicht der Persona.",
		magicLink: "Magic Link",
		copyLink: "Link kopieren",
		accept: "Bewertungen übernehmen",
		accepted: "Übernommen",
		review: "Ansehen",
		questionnaires: "Relevanzbögen",
		noQuestionnaires: "Noch kein Relevanzbogen.",
		noAuthors: "Ohne Empfänger gibt es nichts auszuwählen.",
		scale: {
			much_less: "Irrelevant",
			less: "Weniger wichtig",
			equal: "Durchschnittlich",
			more: "Wichtiger",
			much_more: "Am wichtigsten",
		},
	},
	relevancyFill: {
		title: "Auswahlkriterien für Headless-CMS",
		noteTitle: "Info",
		noteWhat:
			"Bitte teile uns mit, welche der folgenden Themen für Dich zählen. So können wir anschließend prüfen, welche der Systeme den Anforderungen entsprechen.",
		noteWhy:
			"Jedes Headless-CMS erfüllt verschiedenste Kernkriterien. Deine Gewichtung hilft uns, im Auswertungsverfahren auf die Eigenschaften zu fokussieren, die Dir besonders wichtig sind.",
		noteHow:
			"Geh die Themen daher in Ruhe durch und lies die kurze Erläuterung unter jeder Eigenschaft. Bewerte Kriterien von -- (irrelevant) bis ++ (am wichtigsten) und füge bei Bedarf zur Konkretisierung oder Begründung eigene Notizen hinzu.",
		greeting: "Guten Tag",
		progressTitle: "Fortschritt",
		setAll: "Ganzes Thema bewerten",
		addNote: "Anmerkung hinzufügen",
		note: "Anmerkung",
		noteHint: "Optionaler Hinweis für die Auswertung.",
		submit: "Abschließen",
		submitting: "Wird abgeschlossen",
		submitHint:
			"Nach dem Abschließen sehen wir Deine Gewichtung und können die Kriterien bewerten.",
		confirmSubmit:
			"Wirklich abschließen? Antworten können dann nicht mehr bearbeitet werden.",
		confirmYes: "Ja, abschließen",
		loadFailed: "Die Gewichtung konnte nicht geladen werden.",
		noCapabilities: "Dieser Bogen fragt keine Eigenschaft ab.",
		skipToCapabilities: "Direkt zu den Eigenschaften",
	},
	reports: {
		title: "Berichte",
		downloadWorkbook: "Arbeitsmappe herunterladen",
		builder: "Bericht zusammenstellen",
		view: "Ansicht",
		primaryQuestionnaire: "Führender Fragebogen",
		mergeOrder: "Reihenfolge der Quellen",
		mergeOrderHint: "Lücken werden in dieser Reihenfolge gefüllt.",
		knockoutMode: "Umgang mit Anforderungen",
		knockoutEnforce: "Durchsetzen",
		knockoutAnnotate: "Vermerken",
		knockoutIgnore: "Ignorieren",
		asOf: "Stand",
		asOfHint: "Leer bedeutet: aktueller Stand.",
		evaluateNow: "Auswerten",
		persist: "Bericht speichern",
		lock: "Festschreiben",
		locked: "Festgeschrieben",
		live: "Folgt weiterhin Änderungen",
		ranking: "Rangfolge",
		perTopic: "Stufen pro Thema",
		personaRankings: "Rangfolgen je Persona",
		violations: "Verletzte Anforderungen",
		openRequirements: "Offene Anforderungen",
		contributing: "Zugrunde liegende Antworten",
		minimum: "Gefordert",
		actual: "Erreicht",
		excluded: "Erfüllt die Anforderungen nicht",
		requirementsSkipped: "Anforderungen wurden nicht geprüft.",
		formulaVersion: "Formelversion",
		weightedScore: "Gewichtet",
		globalScore: "Global",
		reorderedByRatings: "Reihenfolge weicht ab",
		reorderedHint:
			"Die Gewichtung dieser Persona ordnet die Produkte anders als die globale Auswertung.",
		appliedWeights: "Angewandte Gewichte",
		capabilities: "Eigenschaften im Detail",
		drillDown: "Details",
		noReport: "Noch keine Auswertung.",
		noProducts:
			"Nichts zu bewerten: Kein Produkt fällt in diesen Bericht. Der Eigentümer sieht nur Produkte, die er gelistet hat und die Antworten tragen; prüfen Sie die Produktliste des Kunden und die Ansicht.",
		noAnswers:
			"Nichts zu bewerten: Keine Antwort fällt in diesen Bericht. Prüfen Sie Themen, Eigenschaften und Fragebögen der Ansicht.",
		status: "Status",
		preview: "Vorschau",
		previewHint:
			"So sieht der Kunde den Bericht, sobald er gespeichert ist. Bis dahin bleibt die Auswertung nur hier auf dem Bildschirm.",
		saved: "Gespeicherte Berichte",
	},
	fill: {
		title: "Fragebogen",
		greeting: "Guten Tag",
		progressTitle: "Fortschritt",
		progressAnswered: "beantwortet",
		informational: "Nur zur Information, fließt nicht in die Bewertung ein",
		product: "Produkt",
		productMissing: "Bitte wähle zuerst das Produkt aus, das Du beschreibst.",
		comment: "Kommentar",
		commentHint: "Kurze Anmerkung zu Deiner Antwort.",
		explanation: "Erläuterung",
		explanationHint:
			"Hintergrund, Einschränkungen oder Verweise auf Dokumentation.",
		dontKnowHint:
			"Ohne Auswahl gilt die Frage als „Weiß ich nicht“ und wird nicht bewertet.",
		ratingHint:
			"0 bedeutet nicht unterstützt, 5 bedeutet ohne Aufwand vorhanden.",
		textHint: "Freitext, wird nicht bewertet.",
		multiSelectHint: "Mehrfachauswahl möglich.",
		submit: "Fragebogen abschließen",
		submitting: "Wird abgeschlossen",
		submitHint: "Nach dem Abschließen ist keine Bearbeitung mehr möglich.",
		loadFailed: "Der Fragebogen konnte nicht geladen werden.",
		noQuestions: "Für diesen Fragebogen ist derzeit keine Frage vorgesehen.",
		skipToQuestions: "Direkt zu den Fragen",
	},
	dead: {
		unavailableTitle: "Nicht gefunden",
		unavailableBody:
			"Dieser Fragebogen ist unbekannt oder kann nicht mehr Ausgefüllt werden.",
		submittedTitle: "Vielen Dank",
		submittedBody:
			"Deine Antworten sind bei uns eingegangen. Du kannst dieses Fenster schließen.",
	},
} satisfies Dictionary;
